import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import appStoreImg from "../assets/img/app-store.png";
import googlePlayImg from "../assets/img/google.store.png";
import { trackAppStoreClick, trackGooglePlayClick } from "../analytics/googleAnalytics";
import "./Blog.css";

const questions = [
  {
    id: "goal",
    title: "¿Cuál es tu objetivo?",
    options: ["Perder grasa", "Ganar músculo", "Mantenerse"],
  },
  {
    id: "energy",
    title: "¿Cómo es tu energía por la tarde?",
    options: ["Alta", "Me muero de sueño", "Necesito café"],
  },
  {
    id: "snacking",
    title: "¿Cuántas veces picas entre comidas?",
    options: ["Nunca", "1-2 veces", "Todo el día"],
  },
  {
    id: "sleep",
    title: "¿Te cuesta dormir o te despiertas cansado?",
    options: ["Duermo bien", "Me cuesta dormir", "Siempre cansado"],
  },
  {
    id: "weight_area",
    title: "¿Dónde notas que acumulas más peso?",
    options: ["Abdomen", "Caderas y piernas", "Todo el cuerpo"],
  },
  {
    id: "sweet_cravings",
    title: "¿Sientes ansiedad por el dulce después de comer?",
    options: ["No", "A veces", "Siempre"],
  },
  {
    id: "activity",
    title: "¿Cuál es tu nivel de actividad?",
    options: ["Sedentario", "Moderado", "Atleta"],
  },
  {
    id: "water",
    title: "¿Cuánta agua bebes al día?",
    options: ["Menos de 1L", "Entre 1L y 2L", "Más de 2L"],
  },
  {
    id: "diets",
    title: "¿Has hecho dietas \"milagro\" en el último año?",
    options: ["Sí", "No"],
  },
  {
    id: "age",
    title: "¿Qué edad tienes?",
    options: ["18-24", "25-34", "35-44", "45-54", "55+"],
  },
];

const getResultCopy = (answers) => {
  const wantsMuscle = answers.goal === "Ganar músculo";
  const athlete = answers.activity === "Atleta";
  const lowEnergy =
    answers.energy === "Me muero de sueño" ||
    answers.energy === "Necesito café" ||
    answers.sleep === "Siempre cansado";
  const anxiety =
    answers.sweet_cravings === "Siempre" ||
    answers.snacking === "Todo el día" ||
    answers.sleep === "Me cuesta dormir";

  if (wantsMuscle || athlete) {
    return {
      type: "B",
      title: "Perfil: Motor Metabólico en espera.",
      text: "Tienes potencial pero te falta precisión en los macros. Sin el ratio exacto de proteína, tu músculo no crecerá. Wayo es tu herramienta: saca una foto y asegúrate de que cada comida cuenta. Tu ratio ideal te espera dentro.",
      accent: "#4f46e5",
      background: "linear-gradient(135deg, #312e81 0%, #4f46e5 55%, #22c55e 100%)",
    };
  }

  if (anxiety || lowEnergy) {
    return {
      type: "A",
      title: "Perfil: Metabolismo de Almacenamiento Activado.",
      text: "Tu cuerpo está en modo supervivencia, guardando energía en forma de grasa. El registro manual te estresa y eleva tu cortisol. Wayo es tu solución: escanea, no escribas. Tu plan personalizado para reactivar tu quema de grasa está listo en la app.",
      accent: "#ff4d94",
      background: "linear-gradient(135deg, #831843 0%, #db2777 55%, #fb7185 100%)",
    };
  }

  return {
    type: "A",
    title: "Perfil: Metabolismo de Almacenamiento Activado.",
    text: "Tu cuerpo necesita una estrategia más simple y constante para volver a responder. Wayo reduce el esfuerzo mental: escanea, entiende tus macros y empieza a crear un sistema que sí puedas mantener.",
    accent: "#ff4d94",
    background: "linear-gradient(135deg, #831843 0%, #db2777 55%, #fb7185 100%)",
  };
};

export default function TestMetabolismo() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showLoading, setShowLoading] = useState(false);

  const currentQuestion = questions[step];
  const progress = Math.round((step / questions.length) * 100);

  const result = useMemo(() => getResultCopy(answers), [answers]);

  useEffect(() => {
    if (!showLoading) return undefined;
    const timer = setTimeout(() => {
      setShowLoading(false);
      setStep(questions.length);
    }, 3000);
    return () => clearTimeout(timer);
  }, [showLoading]);

  const handleAnswer = (value) => {
    const nextAnswers = { ...answers, [currentQuestion.id]: value };
    setAnswers(nextAnswers);

    if (step === questions.length - 1) {
      setAnswers(nextAnswers);
      setShowLoading(true);
      return;
    }

    setStep((prev) => prev + 1);
  };

  const goBack = () => {
    if (step > 0 && !showLoading) {
      setStep((prev) => prev - 1);
    }
  };

  return (
    <>
      <Helmet>
        <title>Test de Metabolismo Gratis | Wayo</title>
        <meta
          name="description"
          content="Haz el test de metabolismo de Wayo en 2 minutos y descubre por qué tu dieta no está funcionando. Resultado personalizado y acceso directo a la app."
        />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <link rel="canonical" href="https://wayoai.app/test-metabolismo" />
      </Helmet>

      <main className="blog-page">
        <Header />

        <section className="blog-content">
          <div className="blog-hero" style={{ background: "linear-gradient(135deg, #ff4d94 0%, #ff7eb3 100%)" }}>
            <div className="blog-hero-content">
              <h1 className="blog-hero-title">Test de metabolismo</h1>
              <p className="blog-hero-subtitle">
                Responde 10 preguntas y descubre qué está frenando tus resultados.
              </p>
            </div>
          </div>

          <div className="blog-container">
            <div
              className="card shadow-lg border-0 mx-auto"
              style={{ maxWidth: 820, borderRadius: 24, overflow: "hidden" }}
            >
              <div className="card-body p-4 p-md-5">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <span className="badge rounded-pill text-dark" style={{ background: "#FECA14" }}>
                    Test gratuito
                  </span>
                  <span className="text-muted small">
                    {showLoading ? "Analizando..." : step < questions.length ? `Pregunta ${step + 1} de ${questions.length}` : "Resultado listo"}
                  </span>
                </div>

                <div className="progress mb-4" style={{ height: 10, borderRadius: 999 }}>
                  <div
                    className="progress-bar"
                    role="progressbar"
                    style={{
                      width: `${showLoading ? 100 : progress}%`,
                      background: "linear-gradient(90deg, #ff4d94 0%, #ff7eb3 100%)",
                    }}
                  />
                </div>

                {showLoading ? (
                  <div className="text-center py-5">
                    <div
                      className="spinner-border mb-4"
                      role="status"
                      style={{ width: 64, height: 64, color: "#ff4d94" }}
                    />
                    <h2 className="fw-bold mb-3">Analizando tu perfil metabólico...</h2>
                    <p className="text-muted mb-0">
                      Estamos cruzando tus respuestas para darte la recomendación más útil.
                    </p>
                  </div>
                ) : step < questions.length ? (
                  <>
                    <h2 className="fw-bold mb-4" style={{ fontSize: "2rem", lineHeight: 1.15 }}>
                      {currentQuestion.title}
                    </h2>

                    <div className="d-grid gap-3">
                      {currentQuestion.options.map((option) => (
                        <button
                          key={option}
                          type="button"
                          className="btn btn-light text-start border-0 shadow-sm py-3 px-4"
                          style={{ borderRadius: 18, fontWeight: 600, fontSize: "1.05rem" }}
                          onClick={() => handleAnswer(option)}
                        >
                          {option}
                        </button>
                      ))}
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-4">
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={goBack}
                        disabled={step === 0}
                      >
                        Volver
                      </button>
                      <Link to="/blog" className="text-decoration-none fw-semibold">
                        Volver al blog
                      </Link>
                    </div>
                  </>
                ) : (
                  <div
                    className="text-white p-4 p-md-5"
                    style={{ borderRadius: 24, background: result.background }}
                  >
                    <div className="d-inline-flex align-items-center rounded-pill px-3 py-2 mb-3" style={{ background: "rgba(255,255,255,0.16)" }}>
                      Resultado {result.type}
                    </div>
                    <h2 className="fw-bold mb-3" style={{ fontSize: "2rem", lineHeight: 1.1 }}>
                      {result.title}
                    </h2>
                    <p className="mb-4" style={{ fontSize: "1.1rem", lineHeight: 1.7, color: "rgba(255,255,255,0.95)" }}>
                      {result.text}
                    </p>

                    <div className="d-flex flex-wrap justify-content-center gap-3 mb-3">
                      <a
                        href="https://apps.apple.com/app/id6756500478"
                        className="blog-store-badge test-result-store-badge"
                        target="_blank"
                        rel="noreferrer"
                        onClick={() => trackAppStoreClick("test_metabolismo_result")}
                      >
                        <img src={appStoreImg} alt="Descargar en App Store" />
                      </a>
                      <a
                        href="https://play.google.com/store/apps/details?id=com.facundosolis.wayo"
                        className="blog-store-badge test-result-store-badge"
                        target="_blank"
                        rel="noreferrer"
                        onClick={() => trackGooglePlayClick("test_metabolismo_result")}
                      >
                        <img src={googlePlayImg} alt="Descargar en Google Play" />
                      </a>
                    </div>

                    <div className="text-center">
                      <button
                        type="button"
                        className="btn btn-light fw-bold rounded-pill px-4 py-2"
                        onClick={() => {
                          setAnswers({});
                          setStep(0);
                        }}
                        style={{ color: result.accent }}
                      >
                        Repetir test
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
}
